import { useNavigate } from 'react-router';
import { Button, Card, Chip, Skeleton } from '@heroui/react';
import { Loader2, Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import { PageLayout } from '@/shared/ui';

/**
 * Экраны на время загрузки чанка (`lazyPage` в `app/router`) и куски, которые
 * с ними делят сами страницы: шапка «Мои словари», скелеты фасетов и списков,
 * нижняя панель «Повторить/Учить». Страница и скелет рисуют их одним кодом —
 * загрузка кода и данных идёт одним шиммером, ничего не сдвигается.
 *
 * Файл в стартовом чанке — ничего тяжёлого сюда не тянуть.
 */

/**
 * Какая версия каталога на «Словаре» (см. `DictionariesCatalogV2`): `false` —
 * вернуть старую ленту `DictionariesCatalog`. Здесь, а не в `DictionariesPage`,
 * — скелет загрузки чанка должен рисовать ту же версию, что и страница.
 */
export const CATALOG_V2 = true;

/**
 * Место под панель «Повторить/Учить» внизу ленты — высота кнопок плюс
 * зазор над таб-баром из `StudyBar`, иначе последний ряд уходит под кнопки.
 */
export function StudyBarSpacer() {
  return <div className="h-16 shrink-0" aria-hidden="true" />;
}

export function DictionariesPageSkeleton() {
  const { t } = useTranslation('DictionariesPage');
  return (
    <>
      <PageLayout title={t('title')}>
        {CATALOG_V2 ? (
          /* Та же разметка, что у `DictionariesCatalogV2` до данных. */
          <div className="flex flex-col gap-4">
            <CatalogTabsSkeleton />
            <CatalogFacetsSkeleton />
          </div>
        ) : (
          /* Та же разметка, что у `DictionariesCatalog` до данных. */
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <MyListsHeader onCreate={noop} />
              <MyListsChipsSkeleton />
            </div>
            <div className="border-t border-hairline" />
            <FacetsSkeleton />
          </div>
        )}
        <StudyBarSpacer />
      </PageLayout>
      <StudyBar
        countsLoading
        due={0}
        onReview={noop}
        learn={{ count: 0, loading: false, isDisabled: true, onPress: noop }}
      />
    </>
  );
}

export function MyListsManagePageSkeleton() {
  const { t } = useTranslation('MyListsManagePage');
  const navigate = useNavigate();
  return (
    <PageLayout title={t('title')} onBack={() => void navigate(-1)}>
      <NewDictionaryButton onPress={noop} />
      <ListsSkeleton />
    </PageLayout>
  );
}

/**
 * Заголовок секции своих словарей с карандашом (управление) и плюсом (новый
 * словарь). `grid` — вариант для вкладки «Мои словари» в
 * `DictionariesCatalogV2`: там «Мои словари» уже написано на самой вкладке,
 * поэтому заголовок другой («Личные») и того же размера, что
 * «Уровень»/«Тема» соседней вкладки (12px), а не 14px старой ленты.
 */
export function MyListsHeader({ onCreate, layout = 'chips' }: { onCreate: () => void; layout?: 'chips' | 'grid' }) {
  const { t } = useTranslation('MyListsSection');
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between">
      <h2
        className={`${layout === 'grid' ? 'text-[12px]' : 'text-[14px]'} font-semibold tracking-wide text-ink-subtle uppercase`}
      >
        {layout === 'grid' ? t('gridTitle') : t('title')}
      </h2>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => void navigate(ROUTES.vocabularyLists)}
          aria-label={t('manageDictionaries')}
          className="flex size-6 items-center justify-center rounded-full bg-glass-2 text-ink-subtle active:bg-glass-3"
        >
          <Pencil className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onCreate}
          aria-label={t('newDictionary')}
          className="flex size-6 items-center justify-center rounded-full bg-glass-2 text-ink-subtle active:bg-glass-3"
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function MyListsChipsSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      <Skeleton className="h-9 w-24 rounded-full" />
      <Skeleton className="h-9 w-32 rounded-full" />
    </div>
  );
}

/** Ширина плашек под «Уровень» — 5 коротких пилюль одной ширины (короткий ярлык вроде «A1 917»), как настоящие `ToggleButton size="sm"`. */
const LEVEL_SKELETON_WIDTHS = ['w-16', 'w-16', 'w-16', 'w-16', 'w-16'];

/** Ширина плашек под «Тема» — разброс от короткого («Дом») до длинного («Общество/политика»), с иконкой, в 2-3 обёрнутые строки. */
const TOPIC_SKELETON_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-24', 'w-40', 'w-28', 'w-20', 'w-32'];

/**
 * Один facet в скелете: заголовок — настоящий текст (он известен заранее и
 * ничего не грузит, скелетить нечего), под ним — серые пилюли высотой и
 * формой как у настоящих `ToggleButton size="sm"` (`h-9 rounded-full`,
 * `gap-2` между ними — те же классы, что у `FacetGroup`).
 */
function FacetSkeletonGroup({ title, widths }: { title: string; widths: string[] }) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-[12px] font-semibold tracking-wide text-ink-subtle uppercase">{title}</h2>
      <div className="flex flex-wrap gap-2">
        {widths.map((width, index) => (
          <Skeleton key={index} className={cn('h-9 rounded-full', width)} />
        ))}
      </div>
    </div>
  );
}

/** Скелет самой первой загрузки каталога — размеры пилюль подобраны под реальный контент каждого facet, не абстрактные плашки одного размера. */
export function FacetsSkeleton() {
  const { t } = useTranslation('DictionariesCatalog');
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingFilters')}</span>
      <Skeleton className="h-[17px] w-40" />
      <FacetSkeletonGroup title={t('levelTitle')} widths={LEVEL_SKELETON_WIDTHS} />
      <FacetSkeletonGroup title={t('topicTitle')} widths={TOPIC_SKELETON_WIDTHS} />
    </div>
  );
}

/**
 * Вкладки «Общие словари / Мои словари» на время загрузки — две пилюли шиммера
 * в размер настоящих HeroUI `Tabs`: список 40 px с отступом 4 px, вкладки
 * 32 px со скруглением 24 px, поровну по ширине.
 */
export function CatalogTabsSkeleton() {
  return (
    <div className="flex h-10 p-1" aria-hidden="true">
      <Skeleton className="h-8 flex-1 rounded-3xl" />
      <Skeleton className="h-8 flex-1 rounded-3xl" />
    </div>
  );
}

/** Под «Уровень» в V2 — пилюли `A1 917` шириной 69–79 px одной строкой, без переноса. */
const LEVEL_ROW_SKELETONS = 5;
/** Под «Тема» в V2 — три ряда карточек: первый экран, дальше всё равно под панелью кнопок. */
const TOPIC_CARD_SKELETONS = 9;

/**
 * Скелет вкладки «Общие словари» в `DictionariesCatalogV2` — размеры сняты с
 * загруженной страницы на 375 px: строка счётчика 19.5 px, уровни — строка
 * `h-9` пилюль со скроллом (без переноса), темы — сетка 3 в ряд из карточек
 * `min-h-26`. Заголовки и подсказка «выучено/всего» — настоящий текст.
 */
export function CatalogFacetsSkeleton() {
  const { t } = useTranslation('DictionariesCatalog');
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingFilters')}</span>
      {/* 19.5px — высота строки настоящего счётчика: `text-[13px]` × межстрочный 1.5 от body. */}
      <div className="flex h-[19.5px] items-center">
        <Skeleton className="h-3.5 w-44 rounded-md" />
      </div>
      <div className="flex flex-col gap-2">
        <h2 className="text-[12px] font-semibold tracking-wide text-ink-subtle uppercase">{t('levelTitle')}</h2>
        <div className="-mx-4 flex gap-2 overflow-hidden px-4 pb-1">
          {Array.from({ length: LEVEL_ROW_SKELETONS }, (_, index) => (
            <Skeleton key={index} className="h-9 w-[74px] shrink-0 rounded-full" />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[12px] font-semibold tracking-wide text-ink-subtle uppercase">{t('topicTitle')}</h2>
          <p className="text-[13px] text-ink-subtle">{t('learnedOfTotal')}</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: TOPIC_CARD_SKELETONS }, (_, index) => (
            <Skeleton key={index} className="h-26 rounded-2xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

export interface StudyBarLearn {
  count: number;
  loading: boolean;
  isDisabled: boolean;
  onPress: () => void;
}

/**
 * Две независимые кнопки бок о бок (`flex gap-2`), каждая со своим
 * скруглением — раньше был `ButtonGroup` с общим контейнером и
 * разделителем между кнопками, по решению Дмитрия заменили на просто
 * две кнопки. Фон каждой кнопки зависит от её состояния — задизейбленная
 * серая, активная белая. `disabled:opacity-100` отменяет базовое
 * HeroUI-затемнение disabled-кнопок (`status-disabled` даёт
 * `opacity: 0.5` на всю кнопку) — без него серый `bg-glass-3` выглядел
 * полупрозрачным, а не сплошным.
 */
export function StudyBar({
  countsLoading,
  due,
  onReview,
  learn,
}: {
  countsLoading: boolean;
  due: number;
  onReview: () => void;
  learn: StudyBarLearn;
}) {
  const { t } = useTranslation('DictionariesPage');
  return (
    // Над таб-баром, а не у края экрана: «Словарь» — вкладка, панель табов под ним.
    <div className="fixed inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-[calc(var(--safe-bottom)+var(--tabbar-height)+1rem)]">
      <div className="flex w-full max-w-md gap-2">
        <Button
          isDisabled={countsLoading || !due}
          onPress={onReview}
          className={cn(
            'flex-1 h-11 md:h-11 rounded-3xl text-base font-semibold text-ink shadow-overlay disabled:opacity-100',
            countsLoading || !due ? 'bg-glass-3' : 'bg-surface',
          )}
        >
          {t('review')}
          {countsLoading ? (
            // Пока не посчитали due — спиннер вместо бейджа с числом, тем же
            // паттерном, что у «Учить»: без него кнопка с нулём на секунду
            // выглядела бы задизейбленной, будто повторять нечего.
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <Chip size="sm" variant="tertiary" className="bg-glass-3">
              {due}
            </Chip>
          )}
        </Button>
        <Button
          isDisabled={learn.isDisabled}
          onPress={learn.onPress}
          className={cn(
            'flex-1 h-11 md:h-11 rounded-3xl text-base font-semibold shadow-overlay disabled:opacity-100',
            // Главное действие страницы — синее, как «Продолжить» в
            // онбординге (решение Дмитрия): «Повторить» рядом остаётся
            // белой, чтобы из пары было видно, что здесь главное.
            // Задизейбленная — прежняя серая, а не тусклая синяя.
            learn.isDisabled ? 'bg-glass-3 text-ink' : 'bg-signal-blue text-white',
          )}
        >
          {learn.loading ? (
            // Пока считает/добавляет — спиннер вместо всей надписи, а не
            // рядом с ней: бейджа-счётчика в этот момент ещё нет (посчитать
            // не успели), и «Учить» без числа выглядело бы так, будто кнопка
            // просто повисла, а не работает.
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <>
              {t('learn')}
              {/* Бейдж — только когда есть что учить. При нуле его не
                  рисуем вовсе (было: чип с нулём внутри) — сама кнопка уже
                  задизейблена и притушена, отдельно подсвечивать «0» незачем. */}
              {learn.count > 0 && (
                // На синей кнопке бейдж тоже синий: `bg-glass-3` (серый по
                // светлому фону) на ней выглядел грязным пятном.
                <Chip size="sm" variant="tertiary" className="bg-white/20 text-white">
                  {learn.count}
                </Chip>
              )}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function NewDictionaryButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation('MyListsManagePage');
  return (
    <Button variant="ghost" onPress={onPress}>
      <Plus className="size-4" aria-hidden="true" />
      {t('newDictionary')}
    </Button>
  );
}

/** Форма пилюль под реальную карточку словаря (имя + счётчик слов + шеврон), не абстрактный прямоугольник. */
const NAME_SKELETON_WIDTHS = ['w-28', 'w-40'];

export function ListsSkeleton() {
  const { t } = useTranslation('MyListsManagePage');
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingDictionaries')}</span>
      {NAME_SKELETON_WIDTHS.map((width, index) => (
        <Card key={index} className="flex-row items-center gap-3 px-4 py-3.5">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className={cn('h-4 rounded-md', width)} />
            <Skeleton className="h-3 w-14 rounded-md" />
          </div>
          <Skeleton className="size-4 rounded-full" />
        </Card>
      ))}
    </div>
  );
}

function noop() {
  // Код страницы ещё не пришёл — действовать нечем.
}
